<script setup lang="ts">
  const wadYoutubeContainer = ref<HTMLDivElement | null>(null);
  const youtubeStore = useYouTubePlayerStore();
  const route = useRoute();
  const {videoId, videoCurrentTime, videoIsPlaying, showAsPip} = storeToRefs(youtubeStore);

  const props = withDefaults(defineProps<{pipInstance?: boolean}>(), {pipInstance: false});

  const showPlayer = computed(() => {
    return videoId.value && (!props.pipInstance || showAsPip.value);
  })

  const loadYouTubeAPI = (): Promise<void> => {
    return new Promise((resolve) => {
      if (window.YT && window.YT.Player) {
        resolve()
        return
      }

      const tag = document.createElement('script');
      tag.src = 'https://www.youtube.com/iframe_api';
      tag.async = true;
      document.head.appendChild(tag);

      // Assign once to prevent re-triggering
      (window as any).onYouTubeIframeAPIReady = () => {
        resolve()
      }
    })
  }
  const initPlayer = async () => {
    if (!wadYoutubeContainer.value || !videoId.value) {
      return;
    }
    await loadYouTubeAPI();
    wadYoutubeContainer.value.innerHTML = ''
    wadYoutubeContainer.value.id = 'wadYoutubePlayer-' + Date.now()
    const player = new YT.Player(wadYoutubeContainer.value.id, {
        videoId: videoId.value,
        playerVars: {
          autoplay: 0,
          controls: 1,
          start: Math.floor(videoCurrentTime.value),
        },
        events: {
          onReady: () => {
            if (videoIsPlaying.value) {
              player.seekTo(videoCurrentTime.value, true);
              player.playVideo();
            }
          },
          onStateChange: (event: any) => {
            if (event.data === YT.PlayerState.PLAYING) {
              videoIsPlaying.value = true
            } else if (event.data === YT.PlayerState.PAUSED) {
              videoIsPlaying.value = false
            }
          }
        }
    })
    youtubeStore.setVideoPlayer(player)
  }

  onMounted(async () => {
    await nextTick();
    await initPlayer();

    watch(() => route.fullPath, async () => {
      await nextTick()
      await initPlayer();
    })
  })

  const position = ref({ x: 0, y: 0 })
  let dragStart = { x: 0, y: 0 }
  let dragging = false

  const pipStyle = computed(() => {
    if (!props.pipInstance) return {}
    return {
      transform: `translate(${position.value.x}px, ${position.value.y}px)`
    }
  })
  const startDrag = (event: MouseEvent | TouchEvent) => {
    if (!showAsPip.value) {
      return
    }
    const clientX = 'touches' in event ? event.touches[0].clientX : event.clientX
    const clientY = 'touches' in event ? event.touches[0].clientY : event.clientY
    dragging = true
    dragStart = {
      x: clientX - position.value.x,
      y: clientY - position.value.y
    }
    if ('touches' in event) {
      window.addEventListener('touchmove', onDrag)
      window.addEventListener('touchend', stopDrag)
    } else {
      window.addEventListener('mousemove', onDrag)
      window.addEventListener('mouseup', stopDrag)
    }
  }

  const onDrag = (event: MouseEvent | TouchEvent) => {
    if (!dragging) {
      return
    }

    const clientX = 'touches' in event ? event.touches[0].clientX : event.clientX
    const clientY = 'touches' in event ? event.touches[0].clientY : event.clientY
    position.value.x = clientX - dragStart.x
    position.value.y = clientY - dragStart.y
  }

  const stopDrag = (event: MouseEvent | TouchEvent) => {
    dragging = false

    if ('touches' in event || 'changedTouches' in event) {
      window.removeEventListener('touchmove', onDrag)
      window.removeEventListener('touchend', stopDrag)
    } else {
      window.removeEventListener('mousemove', onDrag)
      window.removeEventListener('mouseup', stopDrag)
    }
  }

  const closePip = () => {
    youtubeStore.setCurrentVideo(null, null);
  }
</script>

<template>
  <client-only>
    <div v-if="showPlayer" :class="['wad4-youtube-player', { 'wad4-youtube-player--pip': showAsPip }]" :style="pipStyle">
      <svg v-if="showAsPip" class="wad4-youtube-player__handle" @mousedown="startDrag" @touchstart.prevent="startDrag" width="24px" height="24px" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path fill-rule="evenodd" clip-rule="evenodd" d="M9.5 8C10.3284 8 11 7.32843 11 6.5C11 5.67157 10.3284 5 9.5 5C8.67157 5 8 5.67157 8 6.5C8 7.32843 8.67157 8 9.5 8ZM9.5 14C10.3284 14 11 13.3284 11 12.5C11 11.6716 10.3284 11 9.5 11C8.67157 11 8 11.6716 8 12.5C8 13.3284 8.67157 14 9.5 14ZM11 18.5C11 19.3284 10.3284 20 9.5 20C8.67157 20 8 19.3284 8 18.5C8 17.6716 8.67157 17 9.5 17C10.3284 17 11 17.6716 11 18.5ZM15.5 8C16.3284 8 17 7.32843 17 6.5C17 5.67157 16.3284 5 15.5 5C14.6716 5 14 5.67157 14 6.5C14 7.32843 14.6716 8 15.5 8ZM17 12.5C17 13.3284 16.3284 14 15.5 14C14.6716 14 14 13.3284 14 12.5C14 11.6716 14.6716 11 15.5 11C16.3284 11 17 11.6716 17 12.5ZM15.5 20C16.3284 20 17 19.3284 17 18.5C17 17.6716 16.3284 17 15.5 17C14.6716 17 14 17.6716 14 18.5C14 19.3284 14.6716 20 15.5 20Z" fill="#FFFFFF"/>
      </svg>
      <div id="wadYoutubePlayer" class="wad4-youtube-player__iframe" ref="wadYoutubeContainer"/>
      <button v-if="showAsPip" class="wad4-youtube-player__close" @click="closePip">X</button>
    </div>
  </client-only>
</template>

<style lang="scss">
.wad4-youtube-player {

  width: 100%;
  aspect-ratio: 16/9;

  &__handle {
    position: absolute;
    top: 0.25rem;
    left: 0.25rem;
    background: rgba(0, 0, 0, 0.6);
  }

  &--pip {
    position: fixed;
    bottom: 1.5rem;
    right: 1.5rem;
    width: 320px;
    height: 180px;
    z-index: 1000;
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.25);
    border-radius: 8px;
    overflow: hidden;
    background-color: black;
    cursor: move;
    transform-origin: bottom right;
  }

  &__iframe {
    width: 100%;
    height: 100%;
    border: none;
  }

  &__close {
    position: absolute;
    top: 0.25rem;
    right: 0.25rem;
    background: rgba(0, 0, 0, 0.6);
    border: none;
    color: white;
    font-size: 0.875rem;
    padding: 0.25rem 0.5rem;
    border-radius: 4px;
    cursor: pointer;
  }
}
</style>
